(function () {
  const debounce = (fn, wait = 160) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  const stripHtml = (html) => {
    if (window.stripHtml) return window.stripHtml(html);
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
  };

  const truncate = (text, max) => {
    if (window.truncate) return window.truncate(text, max);
    const clean = (text || '').trim();
    return clean.length <= max ? clean : `${clean.slice(0, max).trim()}…`;
  };

  const formatDate = (value) => {
    if (window.formatPodcastDate) return window.formatPodcastDate(value);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSearchBlob = (episode) => [
    episode.sourceTitle,
    episode.title,
    episode.nativeId,
    episode.id,
    episode.link,
    formatDate(episode.pubDate),
    stripHtml(episode.summary),
    stripHtml(episode.description)
  ].join(' ').toLowerCase();

  const findMatchSnippet = (episode, query) => {
    const cleanQuery = (query || '').trim().toLowerCase();
    if (!cleanQuery) return '';
    const fields = [episode.title, stripHtml(episode.summary), stripHtml(episode.description), episode.link, episode.nativeId].filter(Boolean);
    const tokens = cleanQuery.split(/\s+/).filter((token) => token.length > 1);
    for (const field of fields) {
      const plain = String(field || '').replace(/\s+/g, ' ').trim();
      const lower = plain.toLowerCase();
      const index = lower.indexOf(cleanQuery) >= 0 ? lower.indexOf(cleanQuery) : tokens.map((token) => lower.indexOf(token)).find((i) => i >= 0);
      if (index >= 0) {
        const start = Math.max(0, index - 48);
        const end = Math.min(plain.length, index + cleanQuery.length + 86);
        return `${start > 0 ? '…' : ''}${plain.slice(start, end)}${end < plain.length ? '…' : ''}`;
      }
    }
    return '';
  };

  const applyEpisodeToPanel = ({ panel, episode, feedTitle, fallbackImage }) => {
    if (!panel || !episode) return;
    panel.hidden = false;
    const refs = {
      art: panel.querySelector('[data-podcast-detail-art]'),
      feed: panel.querySelector('[data-podcast-detail-feed-title]'),
      title: panel.querySelector('[data-podcast-detail-title]'),
      meta: panel.querySelector('[data-podcast-detail-meta]'),
      audio: panel.querySelector('[data-podcast-detail-audio]'),
      summary: panel.querySelector('[data-podcast-detail-summary]'),
      notesWrap: panel.querySelector('[data-podcast-detail-notes-wrap]'),
      notes: panel.querySelector('[data-podcast-detail-notes]'),
      link: panel.querySelector('[data-podcast-detail-link]')
    };
    if (refs.art) {
      refs.art.src = episode.image || fallbackImage || '';
      refs.art.alt = episode.title || feedTitle || 'Podcast episode';
    }
    if (refs.feed) refs.feed.textContent = episode.sourceTitle || feedTitle || 'Podcast';
    if (refs.title) refs.title.textContent = episode.title || 'Podcast episode';
    if (refs.meta) refs.meta.textContent = [formatDate(episode.pubDate), episode.duration].filter(Boolean).join(' • ');
    if (refs.audio) {
      if (episode.audio) {
        refs.audio.src = episode.audio;
        refs.audio.hidden = false;
      } else {
        refs.audio.removeAttribute('src');
        refs.audio.hidden = true;
      }
    }
    if (refs.summary) refs.summary.textContent = truncate(stripHtml(episode.summary || episode.description || ''), 360);
    if (refs.notes && refs.notesWrap) {
      const notes = episode.description || episode.summary || '';
      refs.notes.innerHTML = notes;
      refs.notesWrap.hidden = !stripHtml(notes);
    }
    if (refs.link) {
      refs.link.href = episode.link || episode.sourceUrl || '#';
      refs.link.hidden = !(episode.link || episode.sourceUrl);
    }
  };

  const renderPodcastArchive = async (container) => {
    if (container.dataset.archiveInitialized === 'true') return;
    container.dataset.archiveInitialized = 'true';

    const feedUrl = container.dataset.feedUrl || '';
    const partnerFeedUrl = container.dataset.partnerFeedUrl || '';
    const episodeLimit = parseInt(container.dataset.episodeLimit || '100', 10);
    const initialVisible = parseInt(container.dataset.initialVisibleCount || '12', 10);
    const loadingEl = container.querySelector('[data-podcast-archive-loading], .podcast-archive__loading');
    const errorEl = container.querySelector('[data-podcast-archive-error]');
    const featuredPanel = container.querySelector('[data-podcast-archive-featured]');
    const grid = container.querySelector('[data-podcast-archive-grid]');
    const template = container.querySelector('[data-podcast-archive-item-template]');
    const searchInput = container.querySelector('[data-podcast-search]');
    const sourceFilter = container.querySelector('[data-podcast-source-filter]');
    const sortSelect = container.querySelector('[data-podcast-sort]');
    const countEl = container.querySelector('[data-podcast-results-count]');
    const totalEl = container.closest('.podcast-archive')?.querySelector('[data-podcast-archive-total]');
    const loadWrap = container.querySelector('[data-podcast-archive-pagination]');
    const moreButton = container.querySelector('[data-podcast-archive-more]');
    const statusEl = container.querySelector('[data-podcast-archive-page-status]');

    try {
      if (!window.fetchPodcastPayload) throw new Error('Podcast feed loader unavailable');
      const primary = await window.fetchPodcastPayload(feedUrl);
      let partner = null;
      if (partnerFeedUrl) {
        try { partner = await window.fetchPodcastPayload(partnerFeedUrl); } catch (err) { console.warn('Partner podcast archive feed unavailable', err); }
      }

      const primaryEpisodes = (primary.episodes || []).map((ep) => ({ ...ep, sourceTitle: ep.sourceTitle || primary.feed?.title || container.dataset.feedTitle || 'Casual Swinger Podcast' }));
      const partnerEpisodes = partner ? (partner.episodes || []).map((ep) => ({ ...ep, sourceTitle: ep.sourceTitle || partner.feed?.title || 'Partner Podcast' })) : [];
      const allEpisodes = [...primaryEpisodes, ...partnerEpisodes]
        .filter((ep) => ep.title || ep.audio)
        .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate))
        .slice(0, episodeLimit);

      if (!allEpisodes.length) throw new Error('No podcast episodes returned');
      if (totalEl) totalEl.textContent = `${allEpisodes.length} episodes loaded`;

      const sources = [...new Set(allEpisodes.map((ep) => ep.sourceTitle).filter(Boolean))].sort();
      sources.forEach((source) => {
        if (!sourceFilter) return;
        const option = document.createElement('option');
        option.value = source;
        option.textContent = source;
        sourceFilter.appendChild(option);
      });

      let filteredEpisodes = [...allEpisodes];
      let visibleCount = initialVisible;

      const sortEpisodes = (episodes, sortValue) => {
        const copy = [...episodes];
        if (sortValue === 'oldest') return copy.sort((a, b) => new Date(a.pubDate) - new Date(b.pubDate));
        if (sortValue === 'title') return copy.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        return copy.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
      };

      const updateGrid = () => {
        if (!grid || !template) return;
        const query = (searchInput?.value || '').trim();
        grid.innerHTML = '';
        grid.hidden = false;
        const pageItems = filteredEpisodes.slice(0, visibleCount);
        pageItems.forEach((episode) => {
          const fragment = template.content.cloneNode(true);
          const button = fragment.querySelector('[data-podcast-archive-select]');
          const art = fragment.querySelector('[data-podcast-art]');
          const date = fragment.querySelector('[data-podcast-date]');
          const title = fragment.querySelector('[data-podcast-title]');
          const summary = fragment.querySelector('[data-podcast-summary]');
          const chip = fragment.querySelector('[data-podcast-source-chip]');
          const match = fragment.querySelector('[data-podcast-match]');
          if (art) { art.src = episode.image || primary.feed?.image || ''; art.alt = episode.title || episode.sourceTitle || 'Podcast episode'; }
          if (date) date.textContent = [formatDate(episode.pubDate), episode.duration].filter(Boolean).join(' • ');
          if (title) title.textContent = episode.title || 'Podcast episode';
          if (summary) summary.textContent = truncate(stripHtml(episode.summary || episode.description || ''), 142);
          if (chip) chip.textContent = episode.sourceTitle || (episode.season ? `Season ${episode.season}` : 'Episode');
          const snippet = findMatchSnippet(episode, query);
          if (match && snippet) { match.hidden = false; match.textContent = `Matched: ${snippet}`; }
          button?.addEventListener('click', () => {
            applyEpisodeToPanel({ panel: featuredPanel, episode, feedTitle: episode.sourceTitle, fallbackImage: primary.feed?.image });
            featuredPanel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
          grid.appendChild(fragment);
        });

        const shown = Math.min(visibleCount, filteredEpisodes.length);
        if (countEl) countEl.textContent = `${shown} of ${filteredEpisodes.length} episode${filteredEpisodes.length === 1 ? '' : 's'} shown`;
        if (featuredPanel && filteredEpisodes[0]) applyEpisodeToPanel({ panel: featuredPanel, episode: filteredEpisodes[0], feedTitle: filteredEpisodes[0].sourceTitle, fallbackImage: primary.feed?.image });
        if (loadWrap) {
          loadWrap.hidden = shown >= filteredEpisodes.length;
          if (statusEl) statusEl.textContent = `${filteredEpisodes.length - shown} more available`;
        }
      };

      const applyFilters = () => {
        const query = (searchInput?.value || '').trim().toLowerCase();
        const sourceValue = sourceFilter?.value || '';
        const sortValue = sortSelect?.value || 'newest';
        filteredEpisodes = allEpisodes.filter((episode) => {
          const matchesSource = !sourceValue || episode.sourceTitle === sourceValue;
          const matchesQuery = !query || getSearchBlob(episode).includes(query) || query.split(/\s+/).every((token) => getSearchBlob(episode).includes(token));
          return matchesSource && matchesQuery;
        });
        filteredEpisodes = sortEpisodes(filteredEpisodes, sortValue);
        visibleCount = initialVisible;
        updateGrid();
      };

      moreButton?.addEventListener('click', () => { visibleCount += initialVisible; updateGrid(); });
      searchInput?.addEventListener('input', debounce(applyFilters, 160));
      sourceFilter?.addEventListener('change', applyFilters);
      sortSelect?.addEventListener('change', applyFilters);
      applyFilters();
      loadingEl?.remove();
    } catch (error) {
      console.error(error);
      loadingEl?.remove();
      if (errorEl) errorEl.hidden = false;
      if (totalEl) totalEl.textContent = 'Archive temporarily unavailable';
    }
  };

  const init = () => document.querySelectorAll('[data-podcast-archive]').forEach(renderPodcastArchive);
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
